import React, { Fragment, useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Button,
  Label,
  Input,
  FormGroup,
} from "reactstrap";
import { Breadcrumbs } from "../../AbstractElements";
import { useTranslation } from "react-i18next";
import { sentenceCase, titleCase } from "../../utils/case";

// API
import { getSettings, updateSetting } from "../../api/setting.api";

// Draft WYSIWYG
import { toast } from "react-toastify";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import { hasRole } from "../../utils/auth";

const SettingsMain = () => {
  const { t } = useTranslation();
  const canManage = hasRole(
    "superadmin",
    "finance"
  );
  const [loading, setLoading] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [originalData, setOriginalData] = useState(null);

  // const [monthlyRefreshDay, setMonthlyRefreshDay] = useState("");
  const [peakDates, setPeakDates] = useState([]);
  const [holidayDates, setHolidayDates] = useState([]);
  const [mode, setMode] = useState("peak");
  const [regularPeak, setRegularPeak] = useState({
    monday: [],
    tuesday: [],
    wednesday: [],
    thursday: [],
    friday: [],
    saturday: [],
    sunday: [],
  });

  // =============================
  // INIT LOAD
  // =============================
  useEffect(() => {
    const init = async () => {
      try {
        const data = await getSettings();
        setOriginalData(data);

        // const monthlyRefresh = data.find((x) => x.item === "monthly refresh date");
        const peak = data.find((x) => x.item === "peak season");
        const holiday = data.find((x) => x.item === "holiday");

        // MONTHLY REFRESH DATE
        // if (monthlyRefresh?.details) {
        //   setMonthlyRefreshDay(monthlyRefresh.details.day || "");
        // }

        if (peak?.details) {
          setRegularPeak(peak?.details?.regular || {
            monday: [],
            tuesday: [],
            wednesday: [],
            thursday: [],
            friday: [],
            saturday: [],
            sunday: [],
          });
          setPeakDates(flattenDates(peak?.details?.dates));
        }

        if (holiday?.details) {
          setHolidayDates(flattenDates(holiday?.details));
        }

        setIsDirty(false);
      } catch (err) {
        console.error(err);
      }
    };

    init();
  }, []);

  const resetAllFields = () => {
    if (!originalData) return;

    // const monthlyRefresh = originalData.find((x) => x.item === "monthly refresh date");
    const peak = originalData.find((x) => x.item === "peak season");
    const holiday = originalData.find((x) => x.item === "holiday");

    // MONTHLY REFRESH DATE
    // if (monthlyRefresh?.details) {
    //   setMonthlyRefreshDay(monthlyRefresh.details.day || "");
    // }

    if (peak?.details) {
      setPeakDates(flattenDates(peak?.details));
    }

    if (holiday?.details) {
      setHolidayDates(flattenDates(holiday?.details));
    }

    setIsDirty(false);
  };

  const handleDateClick = (info) => {
    if (!canManage) return;
    const date = info.dateStr;

    if (mode === "peak") {
      setPeakDates((prev) =>
        prev.includes(date)
          ? prev.filter((d) => d !== date)
          : [...prev, date]
      );
    }

    if (mode === "holiday") {
      setHolidayDates((prev) =>
        prev.includes(date)
          ? prev.filter((d) => d !== date)
          : [...prev, date]
      );
    }

    setIsDirty(true);
  };

  const handleEventClick = (info) => {
    const date = info.event.startStr; // "YYYY-MM-DD"
    const type = info.event.title; // "peak" or "holiday"

    if (type === "peak") {
      setPeakDates((prev) => prev.filter((d) => d !== date));
    }

    if (type === "holiday") {
      setHolidayDates((prev) => prev.filter((d) => d !== date));
    }

    setIsDirty(true);
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);

      // ================= MONTHLY REFRESH DATE =================
      // if (activeTab === "0") {
      //   await Promise.all([
      //     // MONTHLY REFRESH DATE
      //     updateSetting({
      //       item: "monthly refresh date",
      //       details: {
      //         day: monthlyRefreshDay,
      //       },
      //     }),
      //   ]);
      // }

      // ================= SEASONS & HOLIDAYS =================
      const peakGrouped = groupByYear([...new Set(peakDates)]);
      const holidayGrouped = groupByYear([...new Set(holidayDates)]);

      await updateSetting({
        item: "peak season",
        details: {
          regular: regularPeak,
          dates: peakGrouped
        },
      });

      await updateSetting({
        item: "holiday",
        details: holidayGrouped,
      });

      toast.success("Settings saved successfully");
      setIsDirty(false);
    } catch (err) {
      console.error(err);
      alert(err.message);
    } finally {
      setLoading(false);
    }
  };

  const groupByYear = (dates) => {
    return dates.reduce((acc, date) => {
      const year = date.split("-")[0];
      if (!acc[year]) acc[year] = [];
      acc[year].push(date);
      return acc;
    }, {});
  };

  const flattenDates = (obj) => {
    if (!obj) return [];
    return Object.values(obj).flat();
  };

  const calendarEvents = [
    ...peakDates.map((date) => ({
      title: "peak",
      date,
      color: "#dc3545",
    })),
    ...holidayDates.map((date) => ({
      title: "holiday",
      date,
      color: "#0d6efd",
    })),
  ];

  return (
    <Fragment>
      <Breadcrumbs
        parent={titleCase(t("settings"))}
        title={titleCase(t("settings"))}
        mainTitle={titleCase(t("settings"))}
      />

      <Container fluid>
        {/* MONTHLY REFRESH DATE */}
        {/* <Row>
          <Col md="12" className="mt-4">
            <Label>{titleCase(t("monthly refresh date"))}</Label>

            <Input
              type="number"
              min="1"
              max="31"
              placeholder={titleCase(t("day of month (1-31)"))}
              value={monthlyRefreshDay || ""}
              onChange={(e) => {
                setMonthlyRefreshDay(e.target.value);
                setIsDirty(true);
              }}
            />

            <small className="text-muted">
              {sentenceCase(t("monthly refresh explanation"))}
            </small>
          </Col>
        </Row> */}
        
        {/* ================= ACTIONS ================= */}
        {canManage && (
          <div className="mt-4 text-end d-flex justify-content-end gap-2">
            <Button color="primary" onClick={handleSubmit} disabled={loading}>
              {loading ? "Saving..." : titleCase(t("save"))}
            </Button>
          </div>
        )}

        {/* ================= SEASONS & HOLIDAYS ================= */}
        <Row>
          {/* LEFT SIDE - REGULAR PEAK */}
          {/* <Col md="4">
            <Card>
              <CardBody>
                <h5>{titleCase(t("regular peak"))}</h5>

                {Object.keys(regularPeak).map((day) => (
                  <div key={day} className="mb-4">
                    <Label className="fw-bold">{titleCase(t(day))}</Label>

                    {regularPeak[day].map((range, index) => (
                      <Row key={index} className="align-items-center mb-2">
                        <Col md="5">
                          <Input
                            type="time"
                            value={range.start}
                            onChange={(e) => {
                              const value = e.target.value;
                              setRegularPeak((prev) => {
                                const updated = [...prev[day]];
                                updated[index].start = value ? `${value}:00` : "";
                                return { ...prev, [day]: updated };
                              });
                            }}
                          />
                        </Col>

                        <Col md="5">
                          <Input
                            type="time"
                            value={range.end}
                            onChange={(e) => {
                              const value = e.target.value;
                              setRegularPeak((prev) => {
                                const updated = [...prev[day]];
                                updated[index].end = value ? `${value}:00` : "";
                                return { ...prev, [day]: updated };
                              });
                            }}
                          />
                        </Col>

                        <Col md="2">
                          <Button
                            color="outline-danger"
                            size="sm"
                            onClick={() => {
                              setRegularPeak((prev) => {
                                const updated = prev[day].filter((_, i) => i !== index);
                                return { ...prev, [day]: updated };
                              });
                            }}
                          >
                            ×
                          </Button>
                        </Col>
                      </Row>
                    ))}

                    <p>
                      <Button
                        size="sm"
                        color="outline-primary"
                        onClick={() => {
                          setRegularPeak((prev) => ({
                            ...prev,
                            [day]: [...prev[day], { start: "", end: "" }],
                          }));
                        }}
                      >
                        {titleCase(t("add"))}
                      </Button>
                    </p>
                  </div>
                ))}
              </CardBody>
            </Card>
          </Col> */}

          {/* RIGHT SIDE - FULL CALENDAR */}
          <Col md="12">
            <Card>
              <CardBody>
                <h5>{titleCase(t("dates"))}</h5>

                {canManage && (
                  <>
                  <div className="mb-3 d-flex gap-2">
                    <Button
                      color={mode === "peak" ? "danger" : "outline-danger"}
                      onClick={() => setMode("peak")}
                    >
                      {titleCase(t("peak season"))}
                    </Button>

                    <Button
                      color={mode === "holiday" ? "primary" : "outline-primary"}
                      onClick={() => setMode("holiday")}
                    >
                      {titleCase(t("holiday"))}
                    </Button>
                  </div>

                  <small className="text-muted d-block mb-3">
                    {mode === "peak"
                      ? sentenceCase(t("click dates to mark peak"))
                      : sentenceCase(t("click dates to mark holiday"))}
                  </small>
                  </>
                )}

                <FullCalendar
                  plugins={[dayGridPlugin, interactionPlugin]}
                  initialView="dayGridMonth"
                  events={calendarEvents}
                  dateClick={handleDateClick}
                  eventClick={handleEventClick}
                  height="auto"
                />
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </Fragment>
  );
};

export default SettingsMain;